import React, { useState, useEffect } from "react";
import confetti from "canvas-confetti";
import CustomScratchCard from "./CustomScratchCard";

const ScratchTicketBox = () => {
    const [scratched, setScratched] = useState(false);
    const [points, setPoints] = useState(0);
    const [showResult, setShowResult] = useState(false);
    const [flipped, setFlipped] = useState(false); // Flip state
    const [attemptsLeft, setAttemptsLeft] = useState(6);
    const [isScratching, setIsScratching] = useState(false); // Track if scratching is in progress
    const [nextTryTime, setNextTryTime] = useState<Date | null>(null);
    const [cooldownTimeLeft, setCooldownTimeLeft] = useState<number>(0);

    // Paramètres du ScratchCard
    const settings = {
        width: 250,
        height: 250,
        image: "/dist/image/Gift-Defi.png", // Image à gratter
        finishPercent: 25, // Pourcentage de surface à gratter avant que l'événement soit déclenché
        onStart: () => {
            setIsScratching(true); // Déclenchement du grattage
        },
        onComplete: () => {
            console.log("Grattage terminé, génération des confettis");
            const reward = getRandomPrize(); // Calculer le gain
            setPoints(reward); // Définir les points gagnés
            setScratched(true); // Marquer comme gratté
            setShowResult(true); // Afficher le résultat

            // Lancer les confettis
            confetti({
                particleCount: 150,
                spread: 90,
                origin: { x: 0.5, y: 0.5 },
            });

            // Retourner la box vers la face avant après 5 secondes
            setTimeout(() => {
                setFlipped(false); // Retourner la box après 5 secondes
            }, 5000);

            setIsScratching(false); // Grattage terminé
        },
    };

    const prizeTiers: { points: number; chances: number }[] = [];

    const tierConfigs = [
        { range: [1, 9], step: 2, chances: 9400000 },
        { range: [2, 8], step: 2, chances: 500000 },
        { range: [10, 15], step: 5, chances: 100000 },
        { range: [20, 45], step: 5, chances: 25000 },
        { range: [50, 80], step: 5, chances: 5000 },
        { range: [85, 175], step: 25, chances: 5000 },
        { range: [200, 775], step: 25, chances: 1000 },
        { range: [800, 1600], step: 50, chances: 500 },
        { range: [1700, 2300], step: 100, chances: 450 },
        { range: [2400, 2800], step: 100, chances: 350 },
        { range: [2900, 3200], step: 100, chances: 300 },
        { range: [3300, 3700], step: 100, chances: 250 },
        { range: [3800, 4200], step: 100, chances: 200 },
        { range: [4300, 4700], step: 100, chances: 150 },
        { range: [4800, 5400], step: 100, chances: 100 },
        { range: [5500, 6500], step: 100, chances: 50 },
        { range: [6600, 9900], step: 100, chances: 25 },
        { range: [10000, 10000], step: 1, chances: 1 },
    ];

    for (const {
        range: [start, end],
        step,
        chances,
    } of tierConfigs) {
        const condition =
            step > 0 ? (i: number) => i <= end : (i: number) => i >= end;
        for (let i = start; condition(i); i += step) {
            prizeTiers.push({ points: i, chances });
        }
    }

    function getRandomPrize() {
        const total = prizeTiers.reduce((sum, tier) => sum + tier.chances, 0);
        let rand = Math.floor(Math.random() * total);
        for (const tier of prizeTiers) {
            if (rand < tier.chances) return tier.points;
            rand -= tier.chances;
        }
        return 1;
    }

    // Fonction de gestion du clic sur la box (flip)
    const handleBoxClick = () => {
        if (scratched) {
            // Ne rien faire si le ticket est déjà gratté (pour éviter qu'il retourne avant 5s)
            return;
            if (cooldownTimeLeft > 0) return; // Ne pas flipper si cooldown actif
        } else {
            // Effectuer le flip uniquement si le ticket n'est pas encore gratté
            setFlipped(!flipped);
        }
    };

    useEffect(() => {
        if (!nextTryTime) return;

        const interval = setInterval(() => {
            const secondsLeft = Math.max(
                0,
                Math.floor((nextTryTime.getTime() - Date.now()) / 1000),
            );
            setCooldownTimeLeft(secondsLeft);
            if (secondsLeft <= 0) {
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [nextTryTime]);

    return (
        <div
            className="flip-card w-full h-full min-h-[265px] cursor-pointer rounded-lg shadow-lg hover:scale-[1.02] hover:shadow"
            onClick={handleBoxClick}
        >
            <div className={`flip-card-inner ${flipped ? "flip" : ""}`}>
                {/* Face avant */}
                <div className="flip-card-front bg-white shadow-lg rounded-lg px-2 py-2 border border-gray-200 flex flex-col items-center h-full min-h-[250px] text-center transition-transform hover:scale-[1.02] hover:shadow-xl">
                    <img
                        src="/dist/image/GifDefi.jpg"
                        className="h-24 w-full mb-3 object-contain"
                    />
                    <div className="text-center flex flex-col flex-grow justify-between w-full">
                        <div>
                            <div className="sm:!text-sm md:!text-base font-semibold text-black">
                                Ticket à gratter
                            </div>
                            <div className="sm:!text-sm md:!text-sm text-gray-600 mt-2 mb-3">
                                Tentez de gagnez jusqu'à 10 000 points !
                            </div>
                        </div>

                        <div className="w-full px-2 mt-auto mb-3">
                            <div className="text-xs text-gray-500 mb-0.5 text-center">
                                {attemptsLeft}/4
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden mb-3">
                                <div
                                    className="bg-gradient-to-l from-blue-500 to-indigo-900 h-full rounded-full transition-all duration-300"
                                    style={{
                                        width: `${(attemptsLeft / 4) * 100}%`,
                                    }}
                                />
                            </div>
                            <div className="sm:!text-sm md:!text-sm font-medium text-black leading-8 mt-auto flex items-center justify-center text-center pb-5">
                                ...
                                <img
                                    src="/dist/image/Fav-Infinexia-512x512.webp"
                                    className="w-4 h-4 ml-1 -mt-0.5"
                                    alt="icon"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Face arrière */}
                <div className="flip-card-back bg-white shadow-lg rounded-lg border flex flex-col items-center h-full min-h-[250px] overflow-hidden">
                    <div className="relative w-full h-full mt-2 p-4 flex items-center justify-center">
                        {!showResult ? (
                            <CustomScratchCard
                                onComplete={() => {
                                    const reward = getRandomPrize();
                                    setPoints(reward);
                                    setScratched(true);
                                    setShowResult(true);
                                    confetti({
                                        particleCount: 150,
                                        spread: 90,
                                        origin: { x: 0.5, y: 0.5 },
                                    });
                                    setTimeout(() => {
                                        setFlipped(false);
                                    }, 5000);
                                }}
                            />
                        ) : (
                            <div className="absolute inset-0 bg-white flex items-center justify-center z-10 rounded-xl">
                                <div className="text-2xl font-bold text-black text-center">
                                    <p className="mt-0 pt-0 text-lg font-medium text-center pb-2">
                                        Félicitations
                                    </p>
                                    <p className="text-base font-medium text-center pb-2">
                                        vous avez gagné !
                                    </p>
                                    {points.toLocaleString()}
                                    <img
                                        src="/dist/image/Fav-Infinexia-512x512.webp"
                                        className="w-5 h-5 inline-block ml-1 -mt-1"
                                        alt="pts"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScratchTicketBox;
